import { createClient } from '@supabase/supabase-js'

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY

// Release any orphaned Web Locks from previous sessions
// This prevents the 20-second lock timeout on page load
if (navigator.locks) {
  navigator.locks.query().then(({ held }) => {
    const stale = (held || []).filter(l => l.name?.includes('supabase') || l.name?.includes('sb-'))
    if (stale.length > 0) {
      console.log(`Releasing ${stale.length} orphaned Supabase lock(s)...`)
      // Request and immediately release each stale lock
      stale.forEach(l => {
        navigator.locks.request(l.name, { steal: true }, () => {
          console.log(`Released lock: ${l.name}`)
          return Promise.resolve()
        })
      })
    }
  }).catch(() => {})
}

// ─── Fetch timeouts — the actual fix for the "whole app wedges until tab
// reload" failure mode ────────────────────────────────────────────────────────
//
// supabase-js awaits the access token before EVERY PostgREST call. If a token
// refresh fetch never settles (classic after macOS sleep mid-request: the TCP
// connection is half-open and the browser never errors), the auth client's
// internal deferred stays pending forever and every subsequent query in the
// app hangs — sidebar skeletons, empty module pages, dead refetches. Only a
// tab reload (fresh client) used to escape it.
//
// Aborting the underlying fetch rejects that stuck deferred, the auth client
// retries cleanly, and the app self-heals without a reload. Timeouts are
// URL-aware so large Storage uploads aren't killed by the tight auth/REST
// budgets.
const FETCH_TIMEOUT_RULES = [
  [/\/auth\/v1\//, 20_000],    // token refresh / session — must fail fast
  [/\/rest\/v1\//, 30_000],    // PostgREST queries
]
const FETCH_TIMEOUT_DEFAULT = 120_000  // storage uploads, functions, anything else

function _timeoutFor(url) {
  for (const [re, ms] of FETCH_TIMEOUT_RULES) if (re.test(url)) return ms
  return FETCH_TIMEOUT_DEFAULT
}

function fetchWithTimeout(input, init = {}) {
  const url = typeof input === 'string' ? input : (input?.url || '')
  const controller = new AbortController()
  const timer = setTimeout(
    () => controller.abort(new DOMException('Supabase fetch timeout', 'TimeoutError')),
    _timeoutFor(url)
  )
  // Chain a caller-provided signal so explicit aborts still work
  const callerSignal = init.signal
  if (callerSignal) {
    if (callerSignal.aborted) controller.abort(callerSignal.reason)
    else callerSignal.addEventListener('abort', () => controller.abort(callerSignal.reason), { once: true })
  }
  return fetch(input, { ...init, signal: controller.signal }).finally(() => clearTimeout(timer))
}

// Embed mode: the /embed/* routes run inside a cross-origin iframe in Gmail,
// where storage is partitioned. There, GoTrue's session machinery DEADLOCKS —
// getSession() hangs >6s (confirmed) while a raw REST fetch with a bearer token
// returns 200 in ~60ms. supabase-js awaits getSession() before every query, so
// every query hangs (panel stuck on skeletons / "Resolving thread").
//
// Fix: run the embed client in "bring your own token" mode via the `accessToken`
// option. That makes supabase-js skip GoTrue entirely and just put our bridged
// token in the Authorization header per request — the exact fast path the probe
// proved works. The few auth.* methods the app calls (getSession / getUser /
// setSession / onAuthStateChange) are stubbed below to use the bridged identity
// instead of GoTrue, so AuthContext + InboxSidebar work unchanged.
export const isEmbed = typeof window !== 'undefined' && window.location?.pathname?.startsWith('/embed')

let _embedToken = null
let _embedRefresh = null
let _embedUser = null
let _refreshInFlight = null
const _embedAuthListeners = new Set()

const _jwtExp = (jwt) => { try { return JSON.parse(atob(jwt.split('.')[1])).exp * 1000 } catch { return 0 } }

// Called by the stubbed setSession when the parent bridges a session in.
function _setEmbedToken(accessToken, refreshToken) {
  _embedToken = accessToken || null
  if (refreshToken !== undefined) _embedRefresh = refreshToken || null
  try {
    const p = accessToken ? JSON.parse(atob(accessToken.split('.')[1])) : null
    _embedUser = p ? { id: p.sub, email: p.email || null } : null
  } catch { _embedUser = null }
  const session = _embedToken ? { access_token: _embedToken, token_type: 'bearer', user: _embedUser } : null
  _embedAuthListeners.forEach(fn => { try { fn(session) } catch { /* listener error */ } })
}

// Hand a self-refreshed session back to the extension so the copy it brokers to
// the NEXT iframe (or the next re-bridge) is the live one. Without this the
// parent keeps replaying the session it captured while an OS tab was last open:
// its access_token is expired AND its refresh_token has already been consumed by
// our own refresh (Supabase rotates them), so nothing can revive it and the
// panel silently drops to the anon key.
function _publishSessionToParent() {
  if (!isEmbed || typeof window === 'undefined' || window.parent === window) return
  if (!_embedToken || !_embedRefresh) return
  try {
    // Targeted origin, never '*' — this message carries live credentials.
    window.parent.postMessage(
      { type: 'CHATRONE_EMBED_SESSION_REFRESHED', session: { access_token: _embedToken, refresh_token: _embedRefresh } },
      'https://mail.google.com'
    )
  } catch { /* not in Gmail, or parent gone */ }
}

// Auth posture of the embed, for UI that must not pretend to be signed in.
//   'n/a'     – not the embed
//   'live'    – we hold an unexpired user token; reads and writes work
//   'expired' – we hold a token we can't refresh → every RLS read returns []
//               and every write is denied (42501). Looks empty, not broken.
//   'none'    – the parent never bridged a session
export function embedAuthState() {
  if (!isEmbed) return 'n/a'
  if (!_embedToken) return 'none'
  return _jwtExp(_embedToken) - Date.now() > 0 ? 'live' : 'expired'
}

// The bridged token expires (~1h) and nothing in Gmail refreshes it once the OS
// tab is closed — so every RLS read silently returns empty ("Not in OS", no
// analysis). Refresh it ourselves with the refresh_token via a raw POST (no
// GoTrue lock machinery, which deadlocks in the partitioned iframe). Single-
// flight + only when within 60s of expiry, to avoid refresh-token rotation races.
async function _ensureFreshToken() {
  if (!_embedToken) return null
  if (_jwtExp(_embedToken) - Date.now() > 60_000) return _embedToken
  if (_embedRefresh && !_refreshInFlight) {
    _refreshInFlight = fetch(`${supabaseUrl}/auth/v1/token?grant_type=refresh_token`, {
      method: 'POST',
      headers: { apikey: supabaseAnonKey, 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: _embedRefresh }),
    })
      .then(r => r.ok ? r.json() : null)
      .then(j => {
        if (!j?.access_token) return
        _setEmbedToken(j.access_token, j.refresh_token || _embedRefresh)
        _publishSessionToParent()   // keep the extension's brokered copy current
      })
      .catch(() => { /* keep stale token; a re-bridge may fix it */ })
      .finally(() => { _refreshInFlight = null })
  }
  if (_refreshInFlight) await _refreshInFlight
  // If we STILL hold an expired token (no refresh_token, or the refresh failed),
  // do NOT send it. An expired JWT is rejected at the gateway with 401 for the
  // WHOLE request — even the SECURITY DEFINER find_contacts_by_emails RPC — so
  // every participant falls to "Not in OS" and the analysis banner vanishes.
  // Returning null falls back to the anon key, which since the 2026-08-04 RLS
  // lockdown can no longer read any table — but the SECURITY DEFINER resolve RPC
  // still answers, so participants keep resolving. That is a trap for the eye:
  // the panel looks connected while every table read returns [] and every write
  // is denied with 42501. Ask the parent for a fresh session (throttled) and let
  // embedAuthState() tell the UI to say so.
  if (_jwtExp(_embedToken) - Date.now() <= 0) { _nudgeParentForSession(); return null }
  return _embedToken
}

// Poke the parent for a fresh bridge, at most once every 30s (every query hits
// the accessToken hook, so this would otherwise fire hundreds of times).
let _lastNudge = 0
function _nudgeParentForSession() {
  if (!isEmbed || typeof window === 'undefined' || window.parent === window) return
  if (Date.now() - _lastNudge < 30_000) return
  _lastNudge = Date.now()
  try { window.parent.postMessage({ type: 'CHATRONE_EMBED_NEED_SESSION' }, '*') } catch { /* parent gone */ }
}

export const supabase = isEmbed
  ? createClient(supabaseUrl, supabaseAnonKey, {
      accessToken: async () => (await _ensureFreshToken()) || supabaseAnonKey,
      global: { fetch: fetchWithTimeout },
    })
  : createClient(supabaseUrl, supabaseAnonKey, {
      auth: {
        persistSession: true,
        autoRefreshToken: true,
        detectSessionInUrl: true,
        // Bypass navigator.locks — prevents query hangs caused by orphaned locks
        // Safe for single-user app (no concurrent tab auth races)
        lock: async (_name, _acquireTimeout, fn) => await fn(),
      },
      global: { fetch: fetchWithTimeout },
    })

// In accessToken mode supabase.auth is a proxy that THROWS on any access. The
// app still calls auth.getSession / onAuthStateChange / setSession (AuthContext,
// InboxSidebar), so replace the whole object with a stub backed by the bridged
// identity. REST/RPC don't use .auth in accessToken mode (they use the
// accessToken() option), so this is safe and the queries stay fast.
if (isEmbed) {
  const sessionShape = () => _embedToken ? { access_token: _embedToken, token_type: 'bearer', user: _embedUser } : null
  const authStub = {
    getSession: async () => ({ data: { session: sessionShape() }, error: null }),
    getUser: async () => ({ data: { user: _embedUser }, error: null }),
    // The parent re-bridges every ~4 min and hands back whatever it captured the
    // last time an OS tab was open. That copy is usually OLDER than the token we
    // refreshed ourselves — accepting it would downgrade a live session to an
    // expired one (and clobber the rotated refresh_token with a spent one), which
    // is exactly how the panel ends up silently running on the anon key. Only
    // take a bridged session that expires later than the one we already hold.
    setSession: async ({ access_token, refresh_token } = {}) => {
      const incoming = access_token ? _jwtExp(access_token) : 0
      if (access_token && _embedToken && incoming <= _jwtExp(_embedToken)) {
        return { data: { session: sessionShape() }, error: null }
      }
      _setEmbedToken(access_token, refresh_token)
      return { data: { session: sessionShape() }, error: null }
    },
    signOut: async () => { _setEmbedToken(null); return { error: null } },
    onAuthStateChange: (cb) => {
      const listener = (session) => { try { cb(session ? 'SIGNED_IN' : 'SIGNED_OUT', session) } catch { /* cb error */ } }
      _embedAuthListeners.add(listener)
      Promise.resolve().then(() => listener(sessionShape())) // emit current state
      return { data: { subscription: { unsubscribe: () => _embedAuthListeners.delete(listener) } } }
    },
  }
  Object.defineProperty(supabase, 'auth', { value: authStub, writable: true, configurable: true })
}

// Fresh access token for authenticated API calls (Bearer header). In embed mode
// the auth stub's getSession() returns the bridged token WITHOUT refreshing it,
// so a write can 401 on a stale token; route through _ensureFreshToken() so it
// refreshes first. Returns null when we hold a dead, unrefreshable token — the
// caller should re-bridge from the parent and retry. Non-embed: the normal
// auto-refreshing GoTrue session token.
export async function getAccessToken() {
  if (isEmbed) return await _ensureFreshToken()
  const { data } = await supabase.auth.getSession()
  return data?.session?.access_token || null
}

// Ask the parent (Gmail content script) to re-bridge a fresh OS session and wait
// briefly for it to land (the embed's message handler calls setSession →
// _setEmbedToken). Returns true once we hold a live, unexpired token. Used by
// queuedWrite to recover an in-dock write when the bridged token has aged out but
// the parent has a newer session (its session-capture refreshes on the OS tab).
// Embed-only; a no-op elsewhere.
export async function rebridgeSession() {
  if (!isEmbed || typeof window === 'undefined' || window.parent === window) return false
  const before = _embedToken
  try { window.parent.postMessage({ type: 'CHATRONE_EMBED_NEED_SESSION' }, '*') } catch { return false }
  for (let i = 0; i < 12; i++) { // ~1.8s max
    await new Promise(r => setTimeout(r, 150))
    if (_embedToken && _embedToken !== before && _jwtExp(_embedToken) - Date.now() > 0) return true
  }
  return !!(await _ensureFreshToken()) // parent didn't answer — a refresh may still work
}

// Safe query wrapper — times out after 12s and retries once automatically.
// Drop-in for any supabase.from(...).select(...) call that might hang.
// Returns the same { data, error, count } shape as a raw Supabase query.
export async function safeQuery(queryBuilder, timeoutMs = 12000) {
  const run = (builder) =>
    Promise.race([
      builder,
      new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Query timed out')), timeoutMs)
      ),
    ])
  try {
    return await run(queryBuilder)
  } catch (firstErr) {
    console.warn('safeQuery: first attempt failed, retrying —', firstErr.message)
    try {
      return await run(queryBuilder)
    } catch (err) {
      console.warn('safeQuery: retry failed —', err.message)
      return { data: null, error: err, count: 0 }
    }
  }
}

export default supabase
